/* eslint-disable */
/* tslint:disable */
// @ts-nocheck

import { Constants } from '@pwc/pwc-deals-web-common';
import {
  ConvertedAnalysisType,
  ReportSection,
  Part,
  SectionItem,
} from '@pwc/pwc-deals-report-ui/build/components/ReportPreview/types';
import {
  LIBRARY_ITEM_TYPES,
  loaderSvg,
  mapContentType,
  mapLayoutType,
  mapLibraryItemToKey,
} from '@pwc/pwc-deals-report-ui/build/components/ReportPreview/utils';
import {
  parseLibraryItems,
  richTextDOMParser,
} from 'features/contentLibrary/libraryItemService';
import {
  fixInsight,
  fixPartData,
} from '../../common/helpers/reportPreviewUtils';
import {
  generateHTML,
  generateRichtextHTML,
} from '@pwc/pwc-deals-report-ui/build/components/ReportPreview/DomHelper';
import { POWER_PIVOT_LIBRARY_ITEM_TYPES } from 'features/analyses/common/LibraryItemCard/PowerPivot/constants/PowerPivotTypes';

const getItemFromStore = (id: number) => window.__LIBRARYITEMSTORE__.get(id);

const removeUnusedPlaceholders = (value: string) => {
  const { content, placeholders } = richTextDOMParser(value);

  placeholders.forEach((e) => {
    e.remove();
  });

  return content.body.innerHTML;
};

const parseRichText = (value: string, isPwcUser: boolean) => {
  try {
    return removeUnusedPlaceholders(parseLibraryItems({ value, isPwcUser }));
  } catch (error) {
    return value;
  }
};

const getData = (item: SectionItem, isPwcUser: boolean) => {
  const libraryItemFromStore = getItemFromStore(item.libraryItemId);
  const data =
    item.content ?? item.data ?? libraryItemFromStore?.content ?? null;

  try {
    const parsedData = JSON.parse(data);

    if (item.type === Constants.LIBRARY_ITEM_TYPES.RICH_TEXT.value) {
      return parseRichText(parsedData.RichTextData, isPwcUser);
    }

    return null;
  } catch (e) {
    //
  }

  return parseRichText(data);
};

const mapItem = (
  item: SectionItem & { libraryItemId: number },
  contentType: string
) => {
  return {
    area: item.column ?? item.analysisColumn,
    order: item.order ?? item.analysisColumn,
    key: mapLibraryItemToKey(item.libraryItemId),
  };
};

const findSVGContent = (item: any) => {
  if (!item) return item;
  const svgContent = item.fileContents?.find(
    (el) => el.formatType === 'SVG' && el.id === item.id
  );
  if (svgContent) {
    return svgContent;
  } else {
    const svgContent = item.fileContents?.find((el) => el.formatType === 'SVG');
    return svgContent;
  }
};

export const getConvertedHypotheses = ({
  pageId,
  kfa,
  sections,
  thumbnail,
  libraryItems,
  territory,
  project,
  isPwcUser,
}): ConvertedAnalysisType => {
  const contentType = mapContentType(kfa.type, kfa.subtype);
  const div = document.querySelector(pageId);
  let html = div?.querySelectorAll('.rich-text-library-item-edit');

  const partsToConvert = [...(sections ?? [])];

  if (thumbnail) {
    partsToConvert.push({
      items: [
        {
          libraryItemId: thumbnail.libraryItemId ?? thumbnail.id,
          type: thumbnail.type,
          name: thumbnail.name,
          data: thumbnail.content,
        },
      ],
    });
  }

  const parts = (partsToConvert || []).flatMap((section) => {
    const orderItems = [...section.items].sort((a, b) => {
      if (a.analysisColumn < b.analysisColumn) {
        return -1;
      }
      if (a.analysisColumn > b.analysisColumn) {
        return 1;
      }
      return 0;
    });

    return orderItems.map(
      (item: SectionItem & { libraryItemId: number }): Part => {
        if (!item.libraryItemId && item.id) {
          item.libraryItemId = item.id;
        }

        if (item.libraryItemId) {
          if (item.type) {
            const itemFromStore = getItemFromStore(item.libraryItemId);
            const libraryItem = libraryItems[item.id];

            let type =
              LIBRARY_ITEM_TYPES[`${item?.type ?? itemFromStore?.type}`];

            const name = item?.name ?? itemFromStore?.name;

            const _data = getData(item, isPwcUser);

            let data = libraryItem?.startsWith('<?xml')
              ? libraryItem
              : (libraryItem?.startsWith('data:image') && !_data
                  ? libraryItem
                  : _data) ?? libraryItem;

            const isPowerpiviot = Object.values(
              POWER_PIVOT_LIBRARY_ITEM_TYPES
            ).includes(item?.type ?? itemFromStore?.type);

            if (isPowerpiviot) {
              data = libraryItem ? libraryItem : data;
              type = 'placeholder-image';
            }

            const correction =
              type === 'excel-cell-range' || type === 'excel-named-range'
                ? findSVGContent(itemFromStore)?.value ?? data
                : data;

            if (
              (type === 'power-bi-report' || type === 'power-bi-report-page') &&
              (loaderSvg === libraryItem || !libraryItem)
            ) {
              return {
                type: 'static-image',
                key: `library-item-${item.libraryItemId}`,
                name: '',
                data: loaderSvg,
                id: item.libraryItemId,
              };
            }

            if (type === 'power-bi-report' || type === 'power-bi-report-page') {
              type = 'placeholder-image';
            }

            return {
              type,
              key: mapLibraryItemToKey(item.id ?? item.libraryItemId),
              name,
              data: correction,
            };
          } else {
            return {
              type: 'static-image',
              key: `library-item-${item.libraryItemId}`,
              name: '',
              data: loaderSvg,
              id: item.libraryItemId,
            };
          }
        }
      }
    );
  });

  const insightPositions = [];

  if (kfa.insight) {
    insightPositions.push({
      area: 1,
      key: 'insight-description',
      order: 0,
    });
  }
  if (thumbnail) {
    insightPositions.push({
      area: 2,
      key: mapLibraryItemToKey(thumbnail.libraryItemId ?? thumbnail.id),
      order: thumbnail.order ?? 0,
    });
  }

  const convertedHypotheses: ConvertedAnalysisType = {
    type: 'keyfocusarea',
    content: {
      id: kfa.contentThreadId,
      title: kfa.title,
      modifiedBy: kfa.modifiedBy,
      modifiedDate: kfa.dateModified,
      connections: { insights: [] },
      contentType: 'KFA',
      layout: null,
      notes: null,
      source: null,
      tags: kfa.tags,
      status:
        kfa.versionStatus === Constants.VERSION_STATUS.SHARED.value
          ? 'shared'
          : 'draft',
      sections: [
        ...sections?.map((section) => {
          const newSection: ReportSection = {
            layout: mapLayoutType(section.layoutType) ?? null,
            order: section.order,
            positions: section.items.map((item) => mapItem(item, contentType)),
          };
          return newSection;
        }),
      ],
      parts: [
        {
          type: 'insight-description',
          key: 'insight-description',
          name: null,
          data: `<p>${fixInsight(kfa.insight) ?? ''}</p>`,
        },
        {
          type: 'description',
          key: '',
          name: null,
          data: `<p>${kfa.body ?? ''}</p>`,
        },
      ],
    },
    projectDetail: {
      name: project.name,
      stamped: '',
      territory,
    },
    insightSection: {
      layout: kfa.insightWithItems?.layoutType ?? null,
      order: kfa.insightWithItems?.order ?? 0,
      positions: insightPositions,
    },
  };

  parts.forEach((part, index) => {
    // React mounts and unmounts, invalidating nodes
    if (!html) {
      html = document
        .querySelector(pageId)
        .querySelectorAll('.rich-text-library-item-edit');
    }

    const element = (html || [])[index];
    if (part) {
      part.data =
        part.type !== 'rich-text'
          ? part.data || generateHTML(part.id, libraryItems)
          : generateRichtextHTML(element, libraryItems).replace(/\u2060/g, '');
    }

    if (part.data && part.data !== loaderSvg) {
      let wrapper = document.createElement('div');
      wrapper.innerHTML = part.data;
      const autosync = wrapper.querySelector(
        '.content-item-link.libraryItem-format-type-info'
      );
      if (autosync) {
        wrapper.innerHTML = wrapper.innerHTML.replace(autosync.innerHTML, '');
        part.data = fixPartData(wrapper.innerHTML);
      }
    }

    if (part && part.data === loaderSvg) {
      part = {
        type: 'static-image',
        key: part?.id ? `library-item-${part?.id}` : part.key,
        name: part?.name,
        data: loaderSvg,
        id: part?.id,
      };
    }
    convertedHypotheses.content.parts.push(part);

    /**
     * If !part?.data evaluates to true,
     * then No rendered data for part
     */
  });

  return convertedHypotheses;
};
